import { normalizeCompletionPolicy } from '../../completionPolicy';
import {
    DEFAULT_MEASUREMENT_SCALE_CONFIG,
    MEASUREMENT_ROUNDING_MODE,
    normalizeMeasurementScaleConfig,
    validateMeasurementScaleConfig,
} from '../../measurementScale';

export const PHYSICAL_SCALE_MODE = {
    PX: 'px',
    AUTO: 'auto',
    MANUAL: 'manual',
};

export const RULER_ROUNDING_MODE = MEASUREMENT_ROUNDING_MODE;

export const DEFAULT_PHYSICAL_SCALE = {
    ...DEFAULT_MEASUREMENT_SCALE_CONFIG,
};

export const DEFAULT_RULER_CONFIG = {
    instructions: '',
    measurements: [],
    physicalScale: DEFAULT_PHYSICAL_SCALE,
};

function toTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : '';
}

function toPositiveNumber(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

function toNonNegativeInt(v, fallback = 2) {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    const i = Math.trunc(n);
    return i >= 0 ? i : fallback;
}

function normalizeRoundingMode(v) {
    return v === RULER_ROUNDING_MODE.TRUNCATE
        ? RULER_ROUNDING_MODE.TRUNCATE
        : RULER_ROUNDING_MODE.ROUND;
}

export function nextMeasurementSlotId(measurements) {
    const used = new Set(
        (Array.isArray(measurements) ? measurements : [])
            .map((m) => toTrimmedString(m?.id))
            .filter(Boolean)
    );
    let n = 1;
    while (used.has(`slot-${n}`)) n += 1;
    return `slot-${n}`;
}

export function createDraftMeasurementSlot(measurements) {
    const id = nextMeasurementSlotId(measurements);
    return {
        id,
        label: '',
        required: true,
    };
}

export function normalizeRulerPhysicalScaleConfig(physicalScaleConfig) {
    const normalized = normalizeMeasurementScaleConfig(physicalScaleConfig);
    // Ensure we always return a safe object with decimals/roundingMode.
    return {
        ...DEFAULT_PHYSICAL_SCALE,
        ...normalized,
        decimals: toNonNegativeInt(normalized.decimals, DEFAULT_PHYSICAL_SCALE.decimals),
        roundingMode: normalizeRoundingMode(normalized.roundingMode),
    };
}

export function formatRulerValue(value, precisionPolicy) {
    if (value == null || typeof value !== 'number' || Number.isNaN(value)) return null;
    const decimals = toNonNegativeInt(
        precisionPolicy?.decimals,
        DEFAULT_PHYSICAL_SCALE.decimals
    );
    const roundingMode = normalizeRoundingMode(precisionPolicy?.roundingMode);
    const factor = 10 ** decimals;
    const rounded = roundingMode === RULER_ROUNDING_MODE.TRUNCATE
        ? Math.trunc(value * factor) / factor
        : Math.round(value * factor) / factor;
    return {
        value: rounded,
        valueString: rounded.toFixed(decimals),
    };
}

export function normalizeMeasurementSlots(measurements) {
    const raw = Array.isArray(measurements) ? measurements : [];
    const seen = new Set();
    return raw.map((m, i) => {
        const preferredId = toTrimmedString(m?.id);
        const fallbackId = `slot-${i + 1}`;
        let id = preferredId || fallbackId;
        if (seen.has(id)) {
            let k = 2;
            while (seen.has(`${id}-${k}`)) k += 1;
            id = `${id}-${k}`;
        }
        seen.add(id);
        const label = toTrimmedString(m?.label) || id;
        return {
            id,
            label,
            required: m?.required !== false,
        };
    });
}

export function normalizeRulerConfig(config) {
    const raw = config && typeof config === 'object' ? config : {};
    return {
        ...DEFAULT_RULER_CONFIG,
        ...raw,
        instructions: typeof raw.instructions === 'string' ? raw.instructions : '',
        measurements: normalizeMeasurementSlots(raw.measurements),
        physicalScale: normalizeRulerPhysicalScaleConfig(raw.physicalScale),
        completionPolicy: normalizeCompletionPolicy(raw),
    };
}

/**
 * Structural validation for `physicalScale` only (shared by Ruler and BoundingBox UIs).
 * @param {object} [physicalScaleConfig]
 * @returns {{code: string, message: string}[]}
 */
export function validatePhysicalScaleConfig(physicalScaleConfig) {
    return validateMeasurementScaleConfig(physicalScaleConfig);
}

/**
 * @returns {{code: string, message: string}[]}
 */
export function validateRulerConfig(config) {
    const issues = [];
    const raw = config && typeof config === 'object' ? config : {};
    const slots = Array.isArray(raw.measurements) ? raw.measurements : [];
    if (slots.length === 0) {
        issues.push({
            code: 'missing_measurements',
            message: 'Add at least one measurement slot in Annotation UI configuration.',
        });
    }

    const seen = new Set();
    slots.forEach((m, idx) => {
        const id = toTrimmedString(m?.id);
        const label = toTrimmedString(m?.label);
        const row = idx + 1;
        if (!id) {
            issues.push({
                code: 'empty_slot_id',
                message: `Measurement slot ${row} is missing an id.`,
            });
            return;
        }
        if (seen.has(id)) {
            issues.push({
                code: 'duplicate_slot_id',
                message: `Measurement slot id "${id}" is duplicated.`,
            });
        } else {
            seen.add(id);
        }
        if (!label) {
            issues.push({
                code: 'missing_slot_label',
                message: `Measurement slot "${id}" is missing a label.`,
            });
        }
    });

    issues.push(...validatePhysicalScaleConfig(raw.physicalScale));
    return issues;
}

